<i18n locale="en">
{
  "input-labels": {
    "title": "Title",
    "description": "Description",
    "images": "Gallery media",
    "action": "Button",
    "action-placeholder": "Button label"
  }
}
</i18n>

<template>
  <div class="flex-col gap-8">
    <OwnInputContainer
      :label="t('input-labels.title')"
      :errors="mapErrorMessage(v$.title.$errors)"
    >
      <OwnInput v-model="title" :placeholder="t('input-labels.title')" />
    </OwnInputContainer>
    <OwnInputContainer
      :label="t('input-labels.description')"
      :errors="mapErrorMessage(v$.description.$errors)"
    >
      <OwnInput
        v-model="description"
        :placeholder="t('input-labels.description')"
      />
    </OwnInputContainer>
    <OwnInputContainer :label="t('input-labels.images')">
      <GalleryMediaInput
        :brand-id="currentBlock?.brandId ?? ''"
        :items="mediaItems"
        :on-video-ready="addHostedVideo"
        :upload-key="id"
        @change="onChange"
      />
    </OwnInputContainer>
    <ToggleCard
      :label="t('input-labels.action')"
      :model-value="Boolean(currentBlockParams?.action)"
      @update:model-value="toggleAction"
    >
      <ContentActionEditor
        v-if="currentBlockParams?.action"
        class="gallery-editor__action-inputs"
        :model-value="currentBlockParams.action"
        @update="onActionUpdate"
      />
    </ToggleCard>
  </div>
</template>

<script lang="ts" setup>
import { useVuelidate, ValidationArgs } from '@vuelidate/core'
import { refDebounced } from '@vueuse/core'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import ToggleCard from '@/components/cards/ToggleCard.vue'
import { type HostedVideoValue } from '@/features/website-builder/builder/components/hostedVideoPolling'
import { websiteSectionRequiredValue } from '@/features/website-builder/builder/validation/websiteSectionValidators'
import { logError } from '@/logger'
import { useMappedGetter } from '@/store'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { useAnalytics } from '@/utils/useAnalytics'
import { mapErrorMessage } from '@/utils/vuelidate'

import { GallerySection } from '../../../WebsiteBuilder.types'
import ContentActionEditor from '../../content/editor/ContentActionEditor.vue'
import { GalleryCommands } from '../commands/GalleryCommands'

import GalleryMediaInput from './GalleryMediaInput.vue'

const props = defineProps<{
  /**
   * ID getting passed to the editor component
   */
  id: string
}>()

const { t } = useI18n()
const analytics = useAnalytics()

const blockById = useMappedGetter<(id: String) => GallerySection | undefined>(
  'websiteBuilder/blockById'
)
const currentBlockParams = computed(() => blockById.value(props.id)?.params)
const currentBlock = computed(() => blockById.value(props.id))
type GalleryAction = NonNullable<GallerySection['params']['action']>
type GalleryMediaItem = NonNullable<GallerySection['params']['media']>[number]

const mediaItems = computed<GalleryMediaItem[]>(() => {
  return currentBlockParams.value?.media ?? []
})

const rules: ValidationArgs<{ description: string; title: string }> = {
  description: {},
  title: {
    required: websiteSectionRequiredValue,
  },
}
const description = ref<string>()
const descriptionDebounced = refDebounced(description, 250)
const title = ref<string>()
const titleDebounced = refDebounced(title, 250)
const v$ = useVuelidate(
  rules,
  { description: descriptionDebounced, title: titleDebounced },
  { $autoDirty: true }
)
onMounted(() => {
  description.value = currentBlockParams.value?.description ?? undefined
  title.value = currentBlockParams.value?.title
})

const fireAnalyticsEvent = (type: 'upload' | 'remove' | 'reorder') => {
  switch (type) {
    case 'upload':
      analytics.track('Gallery Image Added')
      break
    case 'remove':
      analytics.track('Gallery Image Removed')
      break
    case 'reorder':
      analytics.track('Gallery Images Reordered')
      break
    default:
      logError(
        new Error(`Gallery Editor - Unsupported analytics event: ${type}`)
      )
  }
}
const onChange = async ({
  data,
  type,
}: {
  data: GalleryMediaItem[]
  type: 'upload' | 'remove' | 'reorder'
}) => {
  await GalleryCommands.updateBlock({
    targetId: props.id,
    update: {
      params: {
        media: data,
      },
    },
  })

  fireAnalyticsEvent(type)
}

const addHostedVideo = async (video: HostedVideoValue): Promise<void> => {
  await onChange({
    data: [
      ...mediaItems.value,
      { params: { alt: null, ...video }, type: 'video' },
    ],
    type: 'upload',
  })
}

watch(descriptionDebounced, async (value) => {
  if (!v$.value.$validate()) {
    return
  }

  await GalleryCommands.updateBlock({
    targetId: props.id,
    update: {
      params: {
        description: value ?? null,
      },
    },
  })
})
watch(titleDebounced, async (value) => {
  if (!v$.value.$validate()) {
    return
  }

  await GalleryCommands.updateBlock({
    targetId: props.id,
    update: {
      params: {
        title: value,
      },
    },
  })
})

const setAction = async (action: GalleryAction | null) => {
  await GalleryCommands.updateBlock({
    targetId: props.id,
    update: {
      params: {
        action,
      },
    },
  })
}

const createDefaultAction = (): GalleryAction => ({
  params: {
    label: t('input-labels.action-placeholder'),
    url: '',
  },
  type: 'external',
})

const toggleAction = async (enabled: boolean) => {
  if (enabled) {
    await setAction(currentBlockParams.value?.action ?? createDefaultAction())
    return
  }

  await setAction(null)
}

const onActionUpdate = async (action: GalleryAction) => {
  await setAction(action)
}
</script>

<style scoped>
.gallery-editor__action-inputs {
  margin-top: 24px;
}
</style>
